import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Mail, ArrowLeft, RefreshCw, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { authService } from '../services';
import { getUrlForDashboard, getExpectedDashboardForRole, getCurrentPort, getPortForDashboard } from '../utils/portUtils';
import { getDashboardRoute } from '../utils/roleNavigation';

const OTPPage = ({ dashboardType = 'admin' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithOtp } = useAuth();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes in seconds
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [devOtp, setDevOtp] = useState(null); // Store OTP for development mode
  const inputRefs = useRef([]);

  // Get contact info from location state
  // Use loginType if available (from detected type), otherwise fall back to loginOption
  const loginType = location.state?.loginType || location.state?.loginOption || 'email';
  const loginOption = loginType; // Keep loginOption for backward compatibility
  const contact = location.state?.contact || location.state?.email || (loginOption === 'email' ? 'your email' : 'your phone');
  const userId = location.state?.userId;
  const password = location.state?.password; // Get password from location state
  const isRegistration = location.state?.type === 'registration';

  // For registration, use email as contact (since registration always uses email verification)
  const displayContact = isRegistration ? location.state?.email : contact;

  // Extract OTP from location state if available (for development mode)
  const initialOtp = location.state?.otp;

  // Set OTP in development mode
  useEffect(() => {
    if (initialOtp) {
      setDevOtp(initialOtp);
      console.log('🔐 Development Mode - OTP:', initialOtp);
    }
  }, [initialOtp]);

  // Add this to the state variables at the top
  const fromPasswordReset = location.state?.fromPasswordReset;
  const newPassword = location.state?.password;

  // Add this useEffect to handle fromPasswordReset
  useEffect(() => {
    const fromPasswordReset = location.state?.fromPasswordReset;
    const isFirstLogin = location.state?.isFirstLogin;
    
    if (fromPasswordReset || isFirstLogin) {
      // Automatically send OTP for password reset flow
      const sendOTP = async () => {
        try {
          setIsLoading(true);
          const result = await authService.sendLoginOtp({
            contact: location.state?.contact || location.state?.email,
            password: location.state?.password,
            loginType: location.state?.loginType || 'email',
            // Tells the backend to send a real OTP even for a non-admin role —
            // by this point first_login_completed is already true (the reset
            // step itself set it), so the backend can't infer this on its own.
            isFirstLogin: true
          });
          
          if (result.success) {
            console.log('OTP sent successfully after password reset');
          }
        } catch (error) {
          console.error('Error sending OTP after password reset:', error);
          setError(error.message || 'Failed to send OTP');
        } finally {
          setIsLoading(false);
        }
      };
      
      sendOTP();
    }
  }, [location.state]);

  // Get dashboard-specific branding
  const getDashboardBranding = () => {
    switch (dashboardType) {
      case 'admin':
        return {
          primaryColor: 'bg-blue-800',
          primaryColorHover: 'hover:bg-blue-800',
          primaryColorLight: 'bg-blue-50',
          primaryColorText: 'text-blue-900',
          primaryColorBorder: 'border-blue-900',
          primaryColorRing: 'focus:ring-blue-900',
          primaryColorBg: 'bg-blue-600/5',
          primaryColorBorderHover: 'hover:border-blue-900'
        };
      case 'hospital':
        return {
          primaryColor: 'bg-blue-800',
          primaryColorHover: 'hover:bg-blue-800',
          primaryColorLight: 'bg-blue-50',
          primaryColorText: 'text-blue-900',
          primaryColorBorder: 'border-blue-900',
          primaryColorRing: 'focus:ring-blue-900',
          primaryColorBg: 'bg-blue-600/5',
          primaryColorBorderHover: 'hover:border-blue-800'
        };
      case 'fleet':
        return {
          primaryColor: 'bg-blue-800',
          primaryColorHover: 'hover:bg-blue-800',
          primaryColorLight: 'bg-green-50',
          primaryColorText: 'text-blue-900',
          primaryColorBorder: 'border-blue-900',
          primaryColorRing: 'focus:ring-blue-900',
          primaryColorBg: 'bg-blue-600/5',
          primaryColorBorderHover: 'hover:border-blue-800'
        };
      default:
        return {
          primaryColor: 'bg-[#1e3a8a]',
          primaryColorHover: 'hover:bg-[#1e3a8a]/90',
          primaryColorLight: 'bg-blue-50',
          primaryColorText: 'text-[#1e3a8a]',
          primaryColorBorder: 'border-[#1e3a8a]',
          primaryColorRing: 'focus:ring-[#1e3a8a]',
          primaryColorBg: 'bg-[#1e3a8a]/5',
          primaryColorBorderHover: 'hover:border-blue-400'
        };
    }
  };

  const branding = getDashboardBranding();

  // Initial loading effect - remove artificial delay
  useEffect(() => {
    setIsInitialLoading(false);
  }, []);

  // Timer countdown
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft]);

  // Auto-focus first input on mount
  useEffect(() => {
    if (inputRefs.current[0] && !isInitialLoading) {
      inputRefs.current[0].focus();
    }
  }, [isInitialLoading]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Update Frontend/src/pages/OTPPage.jsx - handleOtpChange function
  const handleOtpChange = (index, value) => {
    // Only allow single digit
    if (value.length > 1) return;
    
    // Only allow numbers
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all fields are filled - FIX: Pass newOtp directly
    if (newOtp.every(digit => digit !== '') && newOtp.length === 6) {
      // Call handleVerifyOtpWithData with the new OTP data directly
      handleVerifyOtpWithData(newOtp.join(''));
    }
  };

  // Add this new function to handle 123456 bypass
  const handle123456Bypass = () => {
    const newOtp = ['1', '2', '3', '4', '5', '6'];
    setOtp(newOtp);
    setError('');
    
    // Auto-submit immediately
    handleVerifyOtp();
  };

  // Update the handleKeyDown function
  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    
    // Handle paste
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handlePaste(e);
    }
    
    // Handle 123456 bypass - if user types 123456 in any field
    if (e.key === 'Enter' && otp.join('') === '123456') {
      handle123456Bypass();
    }
  };

  // Add this new function to detect 123456 input
  const handleInput = (index, e) => {
    const value = e.target.value;
    
    // If user types 123456 in any field, fill all fields
    if (value === '123456') {
      handle123456Bypass();
      return;
    }
    
    // Otherwise, handle normally
    handleOtpChange(index, value);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '');
    
    if (pastedData.length === 6 && /^\d{6}$/.test(pastedData)) {
      const newOtp = pastedData.split('');
      setOtp(newOtp);
      setError('');
      
      // Focus last input
      inputRefs.current[5]?.focus();
      
      // Auto-verify after a small delay to ensure UI updates
      setTimeout(() => {
        handleVerifyOtp();
      }, 100);
    }
  };

  // Shared OTP verification logic
  const verifyOtpCode = async (otpNumber) => {
    console.log('[OTP PAGE] Verifying OTP:', otpNumber);
    console.log('[OTP PAGE] Login data:', {
      contact,
      otpCode: otpNumber,
      loginType: loginType
    });
    
    if (otpNumber.toString().length !== 6) {
      setError('Please enter a complete 6-digit OTP');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      if (isRegistration) {
        // Complete registration
        const result = await authService.completeRegistration({
          ...location.state.formData,
          email: location.state.email,
          phone: location.state.phone,
          otpCode: otpNumber
        });
        
        console.log('[OTP PAGE] Registration result:', result);
        
        if (result.success) {
          setSuccess(true);
          setTimeout(() => {
            navigate('/login', { 
              state: { 
                message: 'Registration successful! Please login with your credentials.' 
              } 
            });
          }, 2000);
        }
      } else {
        // Login flow: complete login with OTP
        const loginData = {
          contact: contact,
          otpCode: otpNumber,
          loginType: loginType, // Use the detected loginType
          password: password
        };

        console.log('[OTP PAGE] Sending login data:', loginData);
        const result = await loginWithOtp(loginData);
        
        console.log('[OTP PAGE] Login result:', result);
        console.log('[OTP PAGE] Result data:', result.data);
        console.log('[OTP PAGE] Result data user:', result.data?.user);
        
        if (result.success) {
          if (!result.data?.user) {
            throw new Error('User data not found in login response');
          }
          
          const userData = result.data.user;
          const isFirstTimeLogin = result.data.requiresPasswordReset || userData.isFirstTimeLogin || userData.requiresPasswordReset;
          
          if (isFirstTimeLogin) {
            navigate('/first-time-password-reset', { 
              state: { 
                userData: userData,
                fromLogin: true,
                fromOtpVerified: true,
                contact: contact,
                loginType: loginOption
              } 
            });
            return;
          }
          
          setSuccess(true);
          const userRole = userData.role;
          console.log('[OTP PAGE] User role:', userRole);
          
          setTimeout(() => {
            console.log(`[OTPPage] User ${userRole} on correct port, navigating to dashboard route`);
            const dashboardRoute = getDashboardRoute(userRole, userData);
            navigate(dashboardRoute);
          }, 2000);
        } else {
          throw new Error(result.message || 'Login verification failed');
        }
      }
    } catch (error) {
      console.error('[OTP PAGE] OTP verification failed:', error);
      setAttempts(prev => prev + 1);
      if (attempts >= 2) {
        setError('Too many failed attempts. Please request a new OTP.');
        setTimeLeft(0);
      } else {
        setError(error.message || `Invalid OTP. ${2 - attempts} attempts remaining.`);
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle OTP verification from input fields
  const handleVerifyOtp = async () => {
    const otpNumber = parseInt(otp.join(''), 10);
    await verifyOtpCode(otpNumber);
  };

  // Handle OTP verification with specific data
  const handleVerifyOtpWithData = async (otpString) => {
    const otpNumber = parseInt(otpString, 10);
    await verifyOtpCode(otpNumber);
  };

  const handleResendOtp = async () => {
    setIsResending(true);
    setError('');
    setAttempts(0);
    setOtp(['', '', '', '', '', '']);
    setTimeLeft(300);

    try {
      // Get the actual contact information
      let actualContact;
      let otpType, deliveryMethod;
      
      if (isRegistration) {
        // For registration, use email from location state
        actualContact = location.state?.email;
        otpType = 'EMAIL_VERIFICATION';
        deliveryMethod = 'email';
      } else {
        // For login, use the contact from location state
        actualContact = location.state?.contact || location.state?.email;
        if (loginOption === 'email') {
          otpType = 'EMAIL_VERIFICATION';
          deliveryMethod = 'email';
        } else {
          otpType = 'PHONE_VERIFICATION';
          deliveryMethod = 'sms';
        }
      }

      // Validate that we have a proper contact
      if (!actualContact || actualContact.includes('your ')) {
        throw new Error('Contact information not available. Please go back and try again.');
      }

      // Use the resendOtp method
      await authService.resendOtp(actualContact, otpType, deliveryMethod);
      
      // Show success message
      setError('');
      // You could add a success state here if needed
      
      // Focus first input
      inputRefs.current[0]?.focus();
    } catch (error) {
      console.error('Failed to resend OTP:', error);
      setError(error.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <Card className="w-full max-w-sm sm:max-w-md lg:max-w-lg xl:max-w-xl shadow-xl rounded-xl border-0 bg-white/80 backdrop-blur-sm">
          <CardContent className="pt-6 pb-6 sm:pt-8 sm:pb-8 text-center px-4 sm:px-6">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
              <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
            </div>
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
              {isRegistration ? 'Registration Complete!' : 'Login Successful!'}
            </h2>
            <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 px-2 sm:px-0">
              {isRegistration 
                ? 'Your account has been created successfully. Redirecting to login...'
                : 'Welcome back! Redirecting to your dashboard...'
              }
            </p>
            <div className="flex items-center justify-center space-x-2">
              <div className="w-6 h-6 sm:w-8 sm:h-8 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm text-gray-600">Redirecting...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isInitialLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <Card className="w-full max-w-sm sm:max-w-md lg:max-w-lg xl:max-w-xl shadow-xl rounded-xl border-0 bg-white/80 backdrop-blur-sm">
          <CardContent className="pt-6 pb-6 sm:pt-8 sm:pb-8 text-center px-4 sm:px-6">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4" style={{backgroundColor: '#1e3a8a', border: '2px solid white'}}>
              <Mail className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
              Sending Verification Code
            </h2>
            <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 px-2 sm:px-0">
              Please wait while we send the verification code to {contact}
            </p>
            <div className={`w-6 h-6 sm:w-8 sm:h-8 border-2 ${branding.primaryColorBorder} border-t-transparent rounded-full animate-spin mx-auto`}></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <div className="w-full max-w-sm sm:max-w-md lg:max-w-lg xl:max-w-xl">
        <Card className="shadow-xl rounded-xl border-0 bg-white/80 backdrop-blur-sm">
          <CardHeader className="space-y-2 text-center pb-4 sm:pb-6">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4" style={{backgroundColor: '#1e3a8a', border: '2px solid white'}}>
              <Mail className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
            <CardTitle className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
              Verify Your {loginOption === 'email' ? 'Email' : 'Phone'}
            </CardTitle>
            <CardDescription className="text-sm sm:text-base text-gray-600 px-2 sm:px-0">
              We've sent a 6-digit verification code to
            </CardDescription>
            <p className={`text-xs sm:text-sm font-medium ${branding.primaryColorText} break-all px-2 sm:px-0`}>{displayContact}</p>
          </CardHeader>

          <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6">
            {/* Development Mode OTP Display */}
            {devOtp && (
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <p className="text-sm font-semibold text-blue-900">Development Mode</p>
                </div>
                <p className="text-xs text-blue-700 mb-2">Your OTP code is:</p>
                <p className="text-2xl font-bold text-blue-900 text-center tracking-widest bg-white rounded p-3 border border-blue-300">
                  {devOtp}
                </p>
                <p className="text-xs text-blue-600 mt-2 text-center">
                  This is displayed for testing purposes only
                </p>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center space-x-2">
                <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <p className="text-sm text-blue-600">{error}</p>
              </div>
            )}

            {/* OTP Input Fields */}
            <div className="space-y-3 sm:space-y-4">
              <label className="text-sm sm:text-base font-medium text-gray-700 text-center block">
                Enter verification code
              </label>
              <div className="flex justify-center space-x-2 sm:space-x-3 md:space-x-4">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={el => inputRefs.current[index] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleInput(index, e)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    className={`w-8 h-12 sm:w-10 sm:h-14 md:w-12 md:h-16 text-center text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold bg-transparent border-0 border-b-2 focus:outline-none transition-all duration-200 ${
                      error 
                        ? 'border-blue-400 text-blue-600' 
                        : digit 
                          ? `${branding.primaryColorBorder} ${branding.primaryColorText}` 
                          : `border-gray-300 ${branding.primaryColorBorderHover} text-gray-700`
                    }`}
                    disabled={isLoading}
                  />
                ))}
              </div>
            </div>

            {/* Timer */}
            <div className="text-center">
              {timeLeft > 0 ? (
                <div className="flex items-center justify-center space-x-2 text-xs sm:text-sm text-gray-600">
                  <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span>Code expires in {formatTime(timeLeft)}</span>
                </div>
              ) : (
                <p className="text-xs sm:text-sm text-blue-600">Code has expired</p>
              )}
            </div>

            {/* Verify Button */}
            <Button
              onClick={handleVerifyOtp}
              disabled={isLoading || otp.some(digit => digit === '') || timeLeft === 0}
              className={`w-full ${branding.primaryColor} ${branding.primaryColorHover} text-white font-medium py-2.5 sm:py-3 lg:py-4 text-sm sm:text-base rounded-lg transition-all duration-200 transform hover:scale-[1.02] disabled:transform-none disabled:opacity-50`}
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Verifying...</span>
                </div>
              ) : (
                `Verify ${loginOption === 'email' ? 'Email' : 'Phone'}`
              )}
            </Button>

            {/* Resend OTP */}
            <div className="text-center">
              <p className="text-xs sm:text-sm text-gray-600 mb-2">
                Didn't receive the code?
              </p>
              <Button
                onClick={handleResendOtp}
                disabled={isResending}
                variant="outline"
                className="w-full border-gray-200 hover:bg-gray-50 text-gray-700 disabled:opacity-50 py-2 sm:py-2.5 text-sm sm:text-base"
              >
                {isResending ? (
                  <div className="flex items-center space-x-2">
                    <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
                    <span>Resending...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>Resend Code</span>
                  </div>
                )}
              </Button>
            </div>

            {/* Back Button */}
            <div className="text-center pt-3 sm:pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  if (isRegistration) {
                    navigate('/register');
                  } else {
                    navigate('/login');
                  }
                }}
                className={`flex items-center space-x-2 text-xs sm:text-sm text-gray-600 ${branding.primaryColorText} transition-colors mx-auto`}
                disabled={isLoading}
              >
                <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>{isRegistration ? 'Back to Registration' : 'Back to Login'}</span>
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OTPPage;
